import { useEffect, useRef, useState } from "react";
import { FiX } from "react-icons/fi";

const FLUID_JS_URL = "https://cdn.fluidplayer.com/v3/current/fluidplayer.min.js";

const loadFluidScript = () => {
  const existing = document.querySelector(`script[src="${FLUID_JS_URL}"]`);
  if (existing && window.fluidPlayer) return Promise.resolve();
  if (existing) {
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if (window.fluidPlayer) { clearInterval(check); resolve(); }
      }, 100);
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = FLUID_JS_URL;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

export default function LiveTvModal({ streamUrl, channelName, onClose }) {
  const videoIdRef = useRef(`live-tv-${Math.random().toString(36).slice(2)}`);
  const playerRef = useRef(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!streamUrl) return;
    let cancelled = false;

    const setup = async () => {
      try {
        await loadFluidScript();
        if (cancelled || !window.fluidPlayer) return;

        if (playerRef.current?.destroy) {
          try { playerRef.current.destroy(); } catch (_) { }
        }

        playerRef.current = window.fluidPlayer(videoIdRef.current, {
          layoutControls: {
            fillToContainer: true,
            autoPlay: true,
            mute: false,
            playButtonShowing: false,
            allowTheatre: false,
            playbackRateEnabled: false,
            loop: false,
          },
          hls: {
            overrideNative: true,
          },
        });
      } catch (err) {
        console.warn("Fluid Player (LiveTV) failed to load:", err);
        setError(true);
      }
    };

    setup();

    return () => {
      cancelled = true;
      if (playerRef.current?.destroy) {
        try { playerRef.current.destroy(); } catch (_) { }
      }
      playerRef.current = null;
    };
  }, [streamUrl]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 bg-red-600 px-3 py-1 rounded-full text-xs font-bold uppercase">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
              LIVE
            </span>
            <h3 className="text-lg font-semibold text-white">
              {channelName || "The Slay Network Live"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Video Player */}
        <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
          {error ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
              <p className="text-lg font-medium mb-2">Stream Unavailable</p>
              <p className="text-sm">The live stream is currently offline. Please try again later.</p>
            </div>
          ) : (
            <div className="w-full h-full">
              <video
                id={videoIdRef.current}
                className="w-full h-full"
                playsInline
              >
                <source src={streamUrl} type="application/x-mpegURL" />
              </video>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
